import * as THREE from 'three';
import { select, geoMercator, geoPath } from 'd3';
import { rewind } from '@turf/turf';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { widont } from 'journalize';
import scrollama from 'scrollama';
import gsap from 'gsap';

// data
import data from '../../data/data.json';
import textData from '../../data/text.json';
import wallData from '../assets/geodata/border-walls.json';
import texasGeoData from '../assets/geodata/texas.json';
import borderData from '../assets/geodata/border.json';
// import routeData from '../assets/geodata/route.json';
import reservoirData from '../assets/geodata/reservoirs.json';

const rewindData = data => {
  return (data.features = data.features.map(feature => {
    return rewind(feature, { reverse: true });
  }));
};
rewindData(texasGeoData);

/*
// TEXT DATA
*/
const text = textData["scrolly_1"];

/*
// FUNCTIONS
*/
// Function to fetch JSON data
const loadJSON = async url => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return await response.json();
};

/*
// PARAMS
*/
const params = {
  size: {
    w: window.innerWidth,
    h: window.innerHeight,
  },
  keySize: {
    w: 50,
    h: 50,
  },
  view: window.innerWidth / window.innerHeight > 1 ? 'desktop' : 'mobile',
  colors: {
    'existing-texas': '#761c00',
    easements: '#ffffff',
    'easements-construction': '#ff3d00',
  },
  projection: geoMercator().center([75.05, -41.01]),
  camera: {
    fov: 75,
  },
  group: {
    position: {
      x: window.innerWidth / window.innerHeight > 1 ? 10 : 16.63,
      y: window.innerWidth / window.innerHeight > 1 ? 42 : 34.84,
      z: window.innerWidth / window.innerHeight > 1 ? 0 : -29.5,
    },
  },
  wallGeometry: {
    extrudeSettings: {
      steps: 4,
      depth: window.innerWidth / window.innerHeight > 1 ? 1 : 1.8,
      bevelEnabled: true,
      bevelThickness: window.innerWidth / window.innerHeight > 1 ? 0.01 : 0.03,
      bevelSize: window.innerWidth / window.innerHeight > 1 ? 0.01 : 0.03,
      bevelSegments: 1,
    },
  },
  wallMaterial: {
    colors: {
      'existing-texas': {
        r: 0.3843137254901961,
        g: 0.09019607843137255,
        b: 0,
      },
      easements: {
        r: 1,
        g: 1,
        b: 1,
      },
      'easements-construction': {
        r: 1,
        g: 0.23921568627450981,
        b: 0,
      },
    },
    opacity: 1,
  },
  basemap: {
    size: 195.8,
  },
  duration: 1.2,
};

/*
// ELEMENTS
*/
const container = select('#scrolly');
const canvas = container.select('canvas');

// Add step articles elements
const scrollyOverlay = container.select('.scrolly__article');
const outerSteps = scrollyOverlay
  .selectAll('div.step__outer')
  .data(text)
  .join('div')
  .classed('step__outer', true)
  .attr('data-step-index', (d, i) => i);
const innerSteps = outerSteps
  .append('div')
  .classed('step__inner', true)
  .classed('t-links-underlined', true);
innerSteps.append('p')
  .attr('class', 't-copy')
  .html(d => widont(d));

/*
// 3D SETUP
*/

// Scene
const scene = new THREE.Scene();
scene.background = new THREE.Color(0xffffff);

// Scene for the key
const keyScene = new THREE.Scene();

// Camera
const camera = new THREE.PerspectiveCamera(
  params.camera.fov,
  params.size.w / params.size.h,
  0.1,
  1000
);
camera.position.set(10, 0, 50);

const keyCamera = new THREE.PerspectiveCamera(params.camera.fov, 1, 0.1, 1000);
keyCamera.position.set(10, 0, 50);

// Ambient light
const ambientLight = new THREE.AmbientLight(0xffffff, 2); // Soft white light
scene.add(ambientLight);

// Directional light
const light = new THREE.DirectionalLight(0xffffff, 1);
light.position.set(0, 0, 50).normalize();
scene.add(light);

light.shadow.mapSize.width = 1024;
light.shadow.mapSize.height = 1024;
light.shadow.camera.near = 0.5;
light.shadow.camera.far = 50;

// Group
const group = new THREE.Group();
scene.add(group);
group.position.set(
  params.group.position.x,
  params.group.position.y,
  params.group.position.z
);

// Key Group
const keyGroup = new THREE.Group();
keyScene.add(keyGroup);
keyGroup.position.set(10, 20, 0);

// Renderer
const renderer = new THREE.WebGLRenderer({
  antialias: true,
  alpha: true,
});
renderer.setSize(params.size.w, params.size.h);
renderer.setPixelRatio(window.devicePixelRatio);
renderer.setClearColor(0x000000, 0);
renderer.shadowMap.enabled = true;
container
  .select('.scrolly__figure')
  .node()
  .appendChild(renderer.domElement);

// Label Renderer
const labelRenderer = new CSS2DRenderer();
labelRenderer.setSize(params.size.w, params.size.h);
labelRenderer.domElement.style.position = 'absolute';
labelRenderer.domElement.style.top = '0px';
container
  .select('.scrolly__figure')
  .node()
  .appendChild(labelRenderer.domElement);

// Animation loop
const animate = () => {
  requestAnimationFrame(animate);
  labelRenderer.render(scene, camera);
  renderer.render(scene, camera);
};
animate();

// Resize function
window.addEventListener('resize', () => {
  params.size = {
    w: window.innerWidth,
    h: window.innerHeight,
  };
  (params.view =
    window.innerWidth / window.innerHeight > 1 ? 'desktop' : 'mobile'),
    (camera.aspect = params.size.w / params.size.h);
  camera.updateProjectionMatrix();
  renderer.setSize(params.size.w, params.size.h);
  labelRenderer.setSize(params.size.w, params.size.h);
});

/*
// BORDER
*/
const projectedBorderData = borderData.features.map(feature => {
  return {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: feature.geometry.coordinates.map(coords =>
        coords.map(c => params.projection(c))
      ),
    },
  };
});

projectedBorderData.forEach(data => {
  const coords = [];
  data.geometry.coordinates[0].forEach(coord => {
    coords.push(coord[0], -coord[1], 0)
  })
  
  // LineGeometry
  const lineGeometry = new LineGeometry();
  lineGeometry.setPositions(coords);

  // LineMaterial
  const lineMaterial = new LineMaterial({
    color: 0x000000,
    linewidth: 1.5,
    // transparent: true,
    opacity: 1,
  });

  // Line2 object
  const line = new Line2(lineGeometry, lineMaterial);
  line.computeLineDistances();
  line.name = data.properties.state;
  group.add(line);
  line.renderOrder = 1;
})

/*
// ROUTE
*/
// const projectedRouteData = routeData.features.map(feature => {
//   return {
//     ...feature,
//     geometry: {
//       ...feature.geometry,
//       coordinates: feature.geometry.coordinates.map(coords =>
//         coords.map(c => params.projection(c))
//       ),
//     },
//   };
// });

// projectedRouteData[0].geometry.coordinates.forEach(data => {
//   const coords = [];
//   data.forEach(coord => {
//     coords.push(coord[0], -coord[1], 0.05)
//   })
  
//   // LineGeometry
//   const lineGeometry = new LineGeometry();
//   lineGeometry.setPositions(coords);

//   // LineMaterial
//   const lineMaterial = new LineMaterial({
//     color: 0xffffff,
//     linewidth: 10,
//     transparent: true,
//     opacity: 0,
//   });

//   // Line2 object
//   const line = new Line2(lineGeometry, lineMaterial);
//   line.computeLineDistances();
//   line.name = "route";
//   group.add(line);
//   line.renderOrder = 5;
// })

/*
// RESERVOIRS
*/
const projectedReservoirData = reservoirData.features.map(feature => {
  return {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: feature.geometry.coordinates.map(coords =>
        coords.map(c => params.projection(c))
      ),
    },
  };
});

projectedReservoirData.forEach(data => {
  const shape = new THREE.Shape();
  const start = data.geometry.coordinates[0][0];
  shape.moveTo(start[0], -start[1]);
  data.geometry.coordinates[0].slice(1).forEach(([x, y]) => shape.lineTo(x, -y));
  shape.lineTo(start[0], -start[1]);

  const geometry = new THREE.ShapeGeometry(shape);
  const material = new THREE.MeshBasicMaterial({ 
    color: 0x000000,
  });
  const polygon = new THREE.Mesh(geometry, material);
  polygon.name = data.properties.NameEn;
  group.add(polygon)
  polygon.renderOrder = 2;
})

/*
// WALLS
*/
// reproject data
const projectedWallData = wallData.features.map(feature => {
  return {
    ...feature,
    geometry: {
      ...feature.geometry,
      coordinates: feature.geometry.coordinates.map(coords =>
        coords.map(c => params.projection(c))
      ),
    },
  };
});

// add walls
projectedWallData.forEach(data => {
  const shapes = [];
  data.geometry.coordinates.forEach(coordinate => {
    // draw shape
    const shape = new THREE.Shape();
    const start = coordinate[0];
    const middle = coordinate.slice(1, -1);
    const end = coordinate.slice(-1)[0];

    shape.moveTo(start[0], -start[1]);
    middle.forEach(coord => {
      shape.lineTo(coord[0], -coord[1]);
    });
    shape.lineTo(end[0], -end[1]);
    shape.lineTo(end[0], -end[1]);
    middle.reverse().forEach(coord => {
      shape.lineTo(coord[0], -coord[1]);
    });
    shape.lineTo(start[0], -start[1]);

    shapes.push(shape);
  });

  // geometry
  const geometry = new THREE.ExtrudeGeometry(
    shapes,
    params.wallGeometry.extrudeSettings
  );

  // material
  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color(
      params.wallMaterial.colors[`${data.properties.slug}`].r,
      params.wallMaterial.colors[`${data.properties.slug}`].g,
      params.wallMaterial.colors[`${data.properties.slug}`].b
    ).convertSRGBToLinear(),
    metalness: 0,
    roughness: 0.5,
    transparent: true,
    opacity: 0,
  });

  // mesh
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = data.properties.slug;
  mesh.castShadow = true;
  group.add(mesh);
});

/*
// BASEMAP
*/
// geometry
const planeGeometry = new THREE.PlaneGeometry(1, 1);

// material
// placeholder
const placeholderTexture = new THREE.TextureLoader().load(
  'https://s3.us-east-1.amazonaws.com/capybara-test.texastribune.org/features/2024/texas-border-wall-greg-abbott-landowners/assets/images/basemap/basemap_placeholder.jpg'
);
placeholderTexture.colorSpace = THREE.SRGBColorSpace;

const placeholderMaterial = new THREE.MeshBasicMaterial({
  map: placeholderTexture,
});
placeholderMaterial.needsUpdate = true;
placeholderMaterial.polygonOffset = true;
placeholderMaterial.polygonOffsetFactor = 5;
placeholderMaterial.polygonOffsetUnits = 5;

// mesh
const plane = new THREE.Mesh(planeGeometry, placeholderMaterial);
plane.scale.set(params.basemap.size, params.basemap.size, 1);
plane.receiveShadow = true;
plane.renderOrder = 0;
group.add(plane);

// Function to Load Texture Lazily
function loadTexture(url) {
  const loader = new THREE.TextureLoader();
  return new Promise((resolve, reject) => {
    loader.load(
        url,
        texture => resolve(texture),
        error => reject(error)
      );
  });
}

// Lazy Load Texture for a Mesh
async function lazyLoadTexture(mesh) {
  try {
      const texture = await loadTexture(
        'https://s3.us-east-1.amazonaws.com/capybara-test.texastribune.org/features/2024/texas-border-wall-greg-abbott-landowners/assets/images/basemap/basemap.jpg'
      );
      texture.colorSpace = THREE.SRGBColorSpace;
      mesh.material.map = texture;
      mesh.material.needsUpdate = true;
      mesh.material.polygonOffset = true;
      mesh.material.polygonOffsetFactor = 5;
      mesh.material.polygonOffsetUnits = 5;
  } catch (error) {
      console.error(error);
  }
}

lazyLoadTexture(plane);

/*
// MAP ANNOTATION
*/
const labelData = data['mapLabels'];

labelData
  .filter(l => l.view == params.view)
  .filter(l => l.step == 0)
  .forEach(l => {
    // create label div
    const div = select(document.createElement('div'));
    div
      .attr('id', l.id)
      .attr('class', l.class)
      .style('opacity', 0);

    // text
    div
      .append('div')
      .attr('class', 'label__text')
      .attr('id', `label__text__${l.id}`)
      .style('font-size', l.fontSize)
      .style(
        'transform',
        `translate(calc(${l.xOffset * 100}% + 6px), ${l.yOffset *
          l.fontSize}px) rotate(${l.rotate}deg)`
      )
      .style('font-family', l.fontFamily)
      .style('color', l.color)
      .style('font-style', l.fontStyle)
      .style('font-weight', l.fontWeight)
      .style('letter-spacing', `${l.letterSpacing * l.fontSize}px`)
      .style('text-align', l.textAlign)
      .style(
        'text-shadow',
        `1px 1px 1px ${l.textShadowColor}, -1px 1px 1px ${l.textShadowColor}, 1px -1px 1px ${l.textShadowColor}, -1px -1px 1px ${l.textShadowColor}`
      )
      .html(l.text);

    // marker if any
    if (l.marker) {
      div
        .append('div')
        .classed('label__marker', true)
        .classed(`label__marker__${l.class}`, true);
    }

    // reproject
    const xy = params.projection([l.lon, l.lat]);

    // add label
    const label = new CSS2DObject(div.node());
    label.name = l.id;
    label.position.set(xy[0], -xy[1], 0);
    group.add(label);
  });

/*
// MINI MAP
*/
const mapSize = params.size.w > 500 ? 160 : 140;
const padding = 20;

// canvas
const miniMapCanvas = container
  .select('.figure__overlay')
  .select('.mini-map')
  .append('canvas')
  .attr('width', mapSize)
  .attr('height', mapSize);
const ctx = miniMapCanvas.node().getContext('2d');
const miniMapProjection = geoMercator().fitExtent(
  [
    [padding, padding],
    [mapSize - padding, mapSize - padding],
  ],
  texasGeoData
);
const miniMapPathCanvas = geoPath()
  .projection(miniMapProjection)
  .context(ctx)
  .pointRadius(3);
const miniMapPathSvg = geoPath()
  .projection(miniMapProjection)

ctx.shadowBlur = 8;
ctx.shadowColor = 'rgba(255, 255, 255, 0.8)';

texasGeoData.features.forEach((feature, i) => {
  ctx.beginPath();
  miniMapPathCanvas(feature);
  ctx.fillStyle = '#9e9e9e';
  ctx.fill();
  ctx.closePath();
});

// svg
const miniMapSvg = container
  .select('.figure__overlay')
  .select('.mini-map')
  .append('svg')
  .attr('width', mapSize)
  .attr('height', mapSize);

miniMapSvg
  .append('text')
  .attr('x', mapSize / 2 + 15)
  .attr('y', mapSize / 2)
  .attr('text-anchor', 'middle')
  .attr('class', 'texas')
  .text('TEXAS');
const miniMapWalls = miniMapSvg
  .append('g')
  .attr('class', 'walls')
miniMapWalls
  .selectAll('path')
  .data(wallData.features)
  .join('path')
  .attr('d', miniMapPathSvg)
  .attr('fill', 'none')
  .attr('stroke', d => params.colors[`${d.properties.slug}`])
  .attr('stroke-width', params.size.w > 500 ? 3 : 2.5)
  .attr('stroke-linecap', 'round')
  .attr('stroke-opacity', 0)
  .attr('class', d => d.properties.slug);

const miniMapText = miniMapSvg
  .append('g')
  .attr('transform', `translate(${params.size.w > 500 ? 45 : 30}, ${params.size.w > 500 ? 140 : 120})`)
  .attr('class', 'area-shown')
  .style('opacity', 0);

miniMapText
  .append('text')
  .attr('dx', 0)
  .attr('dy', 0)
  .text('Area');
miniMapText
  .append('text')
  .attr('dx', 0)
  .attr('dy', 12)
  .text('shown');

// highlight square
data['otherElements'].map(d => {
  const dat = d;
  const getProjectedPoints = pNum => {
    return miniMapProjection([
      d[`${pNum}`].split(', ').map(coords => +coords)[1],
      d[`${pNum}`].split(', ').map(coords => +coords)[0],
    ]);
  };
  dat.points = [
    getProjectedPoints('p1'),
    getProjectedPoints('p2'),
    getProjectedPoints('p3'),
    getProjectedPoints('p4'),
  ];
  return dat;
});

miniMapSvg
  .append('polygon')
  .attr('points', data['otherElements'][0].points.map(p => p.join(',')).join(' '))
  .attr('stroke', 'black')
  .attr('stroke-width', 2.5)
  .attr('fill', 'none')
  .attr('stroke-linejoin', 'round')
  .attr('stroke-opacity', 0);

/*
// COLOR KEY
*/
const colorKeys = [
  {
    color: params.colors['existing-texas'],
    borderColor: params.colors['existing-texas'],
    desc: 'Existing federal wall',
    slug: 'existing',
  },
  {
    color: params.colors['easements'],
    borderColor: params.colors['easements'],
    desc: 'Additional land acquired',
    slug: 'easements',
  },
  {
    color: params.colors['easements-construction'],
    borderColor: params.colors['easements-construction'],
    desc: 'State wall completed<br>or under construction',
    slug: 'construction',
  },
];
colorKeys.reverse();
const colorKeyDivs = container
  .select('.figure__overlay')
  .select('.color-keys')
  .selectAll('div')
  .data(colorKeys)
  .join('div')
  .attr('class', d => d.slug)
  .classed('color-key', true);
colorKeyDivs
  .append('div')
  .attr('class', 'color-key__color')
  .style('background', d => d.color)
  .style('border-color', d => d.borderColor);
colorKeyDivs
  .append('div')
  .attr('class', 'color-key__text')
  .html(d => d.desc);
colorKeyDivs.attr('transform', `translate(-100%, 0)`);

/**
 * SCROLLER SETUP
 */
const scroller = scrollama();

// Scrollytelling functions
/* Handler for all steps */
const handleStepEnter = (element, index, direction) => {
  // position data
  const stepData = data['3dWalls']
    .filter(d => d.view == params.view)
    .filter(d => d.step == index);

  // move map position
  gsap.to(group.position, {
    x: stepData[0].groupPositionX,
    y: stepData[0].groupPositionY,
    z: stepData[0].groupPositionZ,
    duration: params.duration,
  });
  gsap.to(group.rotation, {
    x: stepData[0].groupRotationX,
    z: stepData[0].groupRotationZ,
    duration: params.duration,
  });

  // set direction key angle
  // gsap.to(keyGroup.rotation, {
  //   x: Math.max(stepData[0].groupRotationX, -0.75), // set max angle
  //   z: stepData[0].groupRotationZ,
  //   duration: params.duration,
  // });

  // const route = group.children.find(c => c.name == "route")
  // gsap.to(route.material, {
  //   opacity: stepData[0].routeOpacity,
  // });

  // set wall opacity
  stepData
    .forEach(d => {
      // 3D map
      const mesh = group.children.find(c => c.name == d.wallId);
      gsap.to(mesh.material, {
        opacity: d.wallOpacity,
        duration: params.duration,
      });
      // const newColor = new THREE.Color(
      //   params.wallMaterial.colors[`${d.wallId}`].r,
      //   params.wallMaterial.colors[`${d.wallId}`].g,
      //   params.wallMaterial.colors[`${d.wallId}`].b
      // ).convertSRGBToLinear()
      // gsap.to(mesh.material.color, {
      //   r: newColor.r,
      //   g: newColor.g,
      //   b: newColor.b,
      //   duration: params.duration,
      // })
      
      // mini map
      miniMapSvg.select(`.${d.wallId}`)
        .transition()
        .duration(params.duration * 1000)
        .attr('stroke-opacity', d.miniMapWallOpacity)
    });

  // label data
  labelData
    .filter(l => l.view == params.view)
    .filter(l => l.step == index)
    .forEach(l => {
      // label div
      gsap.to(`#${l.id}`, {
        opacity: l.opacity,
        duration: params.duration,
      });

      // label text
      container
        .select(`#label__text__${l.id}`)
        .style('font-size', `${l.fontSize}px`);

      gsap.to(`#label__text__${l.id}`, {
        transform: `translate(calc(${l.xOffset * 100}%), ${l.yOffset *
          l.fontSize}px) rotate(${l.rotate}deg)`,
        duration: params.duration,
      });

      // reproject
      const xy = params.projection([l.lon, l.lat]);

      // reposition label
      const label = group.children.find(c => c.name == l.id);
      gsap.to(label.position, {
        x: xy[0],
        y: -xy[1],
        duration: params.duration,
      });
    });

  const otherData = data['otherElements'][index];
  // non-Texas border
  const line = group.children.find(c => c.name == 'Non-Texas');
  gsap.to(line.material, {
    opacity: otherData.nonTexasBorderOpacity,
    duration: params.duration,
  })

  // minimap
  select('.mini-map')
    .transition()
    .duration(params.duration * 1000)
    .style('opacity', otherData.miniMapOpacity);
  select('.mini-map')
    .select('polygon')
    .transition()
    .duration(params.duration * 1000)
    .attr('stroke-opacity', otherData.miniMapStrokeOpacity);
  select('.mini-map')
    .select('.area-shown')
    .transition()
    .duration(params.duration * 1000)
    .style('opacity', otherData.miniMapStrokeOpacity);

  // color key
  const colorKeys = ['construction', 'easements', 'existing'];
  colorKeys.forEach(key => {
    select('.color-keys')
      .select(`.${key}`)
      .transition()
      .duration(params.duration * 1000)
      .style('opacity', otherData[`colorKey__${key}`]);
  })
};

/* Instanciate */
scroller
  .setup({
    step: '#scrolly .scrolly__article .step__outer',
    offset: 0.9,
    debug: false,
  })
  .onStepEnter(({ element, index, direction }) => {
    const event = new CustomEvent('stepin', {
      detail: { direction: direction },
    });
    element.dispatchEvent(event);
    handleStepEnter(element, index, direction);
  })
  .onStepExit(({ element, index, direction }) => {
    const event = new CustomEvent('stepout', {
      detail: { direction: direction },
    });
    element.dispatchEvent(event);
    // handleStepExit(element, index, direction);
  });
